import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Check, 
  Zap, 
  Star, 
  Building2, 
  Sparkles,
  ArrowRight,
  Clock,
  Crown,
  Loader2,
  Globe
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscription Plans Page
 * 
 * Displays all available subscription tiers with regional pricing,
 * features, and upgrade options. Supports monthly/annual billing.
 * Automatically detects user's region for local currency display.
 */
export default function SubscriptionPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [launchInfo, setLaunchInfo] = useState(null);
  
  // Regional pricing state
  const [countryCode, setCountryCode] = useState('US');
  const [currency, setCurrency] = useState('USD');
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [detectingLocation, setDetectingLocation] = useState(true);

  // Detect user's region using multiple methods
  const detectUserRegion = useCallback(async () => {
    setDetectingLocation(true);
    
    // Check for URL parameter override first (for testing)
    const regionParam = searchParams.get('region');
    if (regionParam) {
      setCountryCode(regionParam.toUpperCase());
      setDetectingLocation(false);
      return;
    }
    
    try {
      // Method 1: Try IP-based geolocation
      const geoResponse = await fetch('https://ipapi.co/json/', { timeout: 3000 });
      if (geoResponse.ok) {
        const geoData = await geoResponse.json();
        if (geoData.country_code) {
          setCountryCode(geoData.country_code);
          setDetectingLocation(false);
          return;
        }
      }
    } catch (error) {
      // IP geolocation failed, trying fallback
    }

    try {
      // Method 2: Use browser locale as fallback
      const locale = navigator.language || navigator.userLanguage;
      const localeCountry = locale.split('-')[1]?.toUpperCase();
      
      if (localeCountry) {
        setCountryCode(localeCountry);
      }
    } catch (error) {
      // Locale detection failed
    }
    
    setDetectingLocation(false);
  }, [searchParams]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/plans?country_code=${countryCode}`);
      const data = await response.json();
      setPlans(data.plans || []);
      setCurrency(data.currency || 'USD');
      setCurrencySymbol(data.symbol || '$');
      setLaunchInfo({
        isLaunchPeriod: data.is_launch_period,
        daysRemaining: data.launch_days_remaining,
        message: data.launch_message
      });
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCurrentSubscription(data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, [token]);

  useEffect(() => {
    detectUserRegion();
  }, [detectUserRegion]);

  useEffect(() => {
    if (!detectingLocation) {
      fetchPlans();
    }
    if (token) {
      fetchCurrentSubscription();
    }
  }, [token, detectingLocation, fetchPlans, fetchCurrentSubscription]);

  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/login?redirect=/subscription');
      return;
    }

    if (tier === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setCheckoutLoading(tier);

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier,
          billing_cycle: billingCycle,
          origin_url: window.location.origin,
          country_code: countryCode
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    
    // Format based on currency
    if (currency === 'INR') {
      return `${currencySymbol}${price.toLocaleString('en-IN')}`;
    } else if (currency === 'IDR') {
      return `${currencySymbol}${price.toLocaleString('id-ID')}`;
    } else {
      return `${currencySymbol}${price.toLocaleString(undefined, { minimumFractionDigits: price % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}`;
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'free': return <Sparkles className="w-6 h-6" />;
      case 'creator': return <Star className="w-6 h-6" />;
      case 'pro': return <Zap className="w-6 h-6" />;
      case 'enterprise': return <Building2 className="w-6 h-6" />;
      default: return <Sparkles className="w-6 h-6" />;
    }
  };

  const getPlanColor = (tier) => {
    switch (tier) {
      case 'free': return 'from-gray-500 to-gray-600';
      case 'creator': return 'from-blue-500 to-purple-500';
      case 'pro': return 'from-purple-500 to-pink-500';
      case 'enterprise': return 'from-amber-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Country code to flag emoji
  const getCountryFlag = (code) => {
    const flags = {
      'US': '🇺🇸', 'IN': '🇮🇳', 'GB': '🇬🇧', 'EU': '🇪🇺',
      'BR': '🇧🇷', 'ID': '🇮🇩', 'NG': '🇳🇬', 'PH': '🇵🇭', 'MX': '🇲🇽',
      'DE': '🇩🇪', 'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸'
    };
    return flags[code] || '🌐';
  };

  if (loading || detectingLocation) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">
            {detectingLocation ? 'Detecting your region...' : 'Loading plans...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">
              Choose Your Plan
            </span>
          </h1>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl mx-auto">
            Unlock the full power of AIRA ECHO with a plan that fits your needs
          </p>
          
          {/* Region Indicator */}
          <div className="flex items-center justify-center gap-2 mt-4 text-sm text-[var(--text-tertiary)]">
            <Globe className="w-4 h-4" />
            <span>Prices shown in {currency}</span>
            <span className="text-lg">{getCountryFlag(countryCode)}</span>
          </div>
        </motion.div>

        {/* Launch Period Banner */}
        {launchInfo?.isLaunchPeriod && (
          <motion.div
            className="mb-8 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-semibold">Early Access Period</p>
                <p className="text-[var(--text-secondary)] text-sm">
                  {launchInfo.message} <strong>{launchInfo.daysRemaining} days remaining!</strong>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-[var(--bg-card)] rounded-xl p-1 flex gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--text-secondary)] hover:text-white'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--text-secondary)] hover:text-white'
              }`}
              data-testid="billing-annual"
            >
              Annual
              <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => {
            const isCurrentPlan = currentSubscription?.tier === plan.tier;
            const price = billingCycle === 'monthly' ? plan.monthly_price : plan.annual_price;
            const monthlyEquivalent = billingCycle === 'annual' ? plan.annual_monthly : plan.monthly_price;

            return (
              <motion.div
                key={plan.tier}
                className={`relative rounded-2xl border overflow-hidden ${
                  plan.badge === 'Best Value' 
                    ? 'border-[var(--color-primary)] shadow-lg shadow-[var(--color-primary)]/20' 
                    : 'border-[var(--border-color)]'
                } bg-[var(--bg-card)]`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`plan-${plan.tier}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-lg ${
                    plan.badge === 'Best Value' 
                      ? 'bg-[var(--color-primary)] text-white' 
                      : plan.badge === 'Popular'
                        ? 'bg-blue-500 text-white'
                        : 'bg-amber-500 text-white'
                  }`}>
                    {plan.badge}
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Header */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(plan.tier)} flex items-center justify-center text-white mb-4`}>
                    {getPlanIcon(plan.tier)}
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-[var(--text-tertiary)] mb-4">{plan.tagline}</p>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">
                        {price === 0 ? 'Free' : formatPrice(monthlyEquivalent)}
                      </span>
                      {price > 0 && (
                        <span className="text-[var(--text-tertiary)]">/mo</span>
                      )}
                    </div>
                    {billingCycle === 'annual' && price > 0 && (
                      <p className="text-sm text-green-400 mt-1">
                        {formatPrice(plan.annual_price)} billed annually
                      </p>
                    )}
                    {plan.annual_savings > 0 && billingCycle === 'annual' && (
                      <p className="text-xs text-[var(--text-tertiary)]">
                        Save {formatPrice(plan.annual_savings)}/year
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          plan.highlight_features.some(h => feature.includes(h))
                            ? 'text-[var(--color-primary)]'
                            : 'text-green-400'
                        }`} />
                        <span className={`${
                          plan.highlight_features.some(h => feature.includes(h))
                            ? 'text-white font-medium'
                            : 'text-[var(--text-secondary)]'
                        }`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                    {plan.features.length > 6 && (
                      <li className="text-sm text-[var(--text-tertiary)]">
                        +{plan.features.length - 6} more features
                      </li>
                    )}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={isCurrentPlan || checkoutLoading === plan.tier}
                    className={`w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                      isCurrentPlan
                        ? 'bg-green-500/20 text-green-400 cursor-default'
                        : plan.tier === 'free'
                          ? 'bg-[var(--bg-tertiary)] text-white hover:bg-[var(--bg-hover)]'
                          : 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white hover:opacity-90'
                    }`}
                    data-testid={`upgrade-${plan.tier}`}
                  >
                    {checkoutLoading === plan.tier ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isCurrentPlan ? (
                      <>
                        <Crown className="w-4 h-4" />
                        Current Plan
                      </>
                    ) : plan.tier === 'free' ? (
                      'Get Started Free'
                    ) : (
                      <>
                        Upgrade Now
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Questions?</h2>
          <p className="text-[var(--text-secondary)]">
            Contact us at{' '}
            <a href="mailto:support@askaira.ai" className="text-[var(--color-primary)] hover:underline">
              support@askaira.ai
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
